import { useState } from 'react'
import { Link } from 'react-router'
import { usePool } from '@/components/layout/AppShell'
import { cn } from '@/lib/cn'
import { useCompletedWeeks, useSeasonStandings, useWeekResults, useWeekWinners } from './queries'

const SEASON = 'season'

/* One row shape for both scopes. season_standings and weekly_results answer the
   same questions under different column names (total_correct vs correct_picks),
   so they are flattened here rather than branching inside the markup — and a
   union of two array types is awkward to map over besides. */
interface BoardRow {
  userId: string | null
  rank: number
  correct: number | null
  incorrect: number | null
  note: string | null
  noteIsWarning: boolean
}

/* Record is correct-incorrect, NOT W-L-T.
   `tie_games` is not a third outcome bucket — it counts how many of the week's
   games were ties, and those games are ALREADY inside correct_picks or
   incorrect_picks depending on the pool's tie_policy (all_correct credits them,
   no_credit does not, exclude_game drops them from both). Rendering "12-3-1"
   would therefore double-count every tie. Ties surface as their own dim note
   instead, which is the only form that stays truthful across all three
   policies. */
function record(correct: number | null, incorrect: number | null) {
  return `${correct ?? 0}-${incorrect ?? 0}`
}

function winPct(correct: number | null, incorrect: number | null) {
  const played = (correct ?? 0) + (incorrect ?? 0)
  if (played === 0) return '—'
  // .750, not 75.0% — this is a standings table, and the column is a glyph
  // narrower without the percent sign.
  return ((correct ?? 0) / played).toFixed(3).replace(/^0/, '')
}

function pluralize(count: number, word: string) {
  return `${count} ${count === 1 ? word : `${word}s`}`
}

/* Standard competition ranking: 1, 2, 2, 4. Ties share a rank and the next rank
   skips, which is how a tied week actually reads. Computed here rather than
   fetched because the views publish season_rank and no week_rank — ranking a
   single week is the one thing the database does not already do. Input must
   already be sorted by points descending, which fetchWeekResults guarantees. */
function weekRank(rows: { points: number | null }[], row: { points: number | null }) {
  const points = row.points ?? 0
  return rows.findIndex((candidate) => (candidate.points ?? 0) === points) + 1
}

/* Rank 1 green, 2-3 muted green, everyone else grey. The accent is a left border
   rather than a fill: green means *picked* on the Picks page, and a filled green
   row here would read as "this row is selected". */
function rankAccent(rank: number) {
  if (rank === 1) return 'border-l-field'
  if (rank <= 3) return 'border-l-field/45'
  return 'border-l-rule'
}

export default function BoardPage() {
  const pool = usePool()
  const [selected, setSelected] = useState<string>(SEASON)

  const { data: weeks, isPending: weeksPending } = useCompletedWeeks(pool.id)
  const { data: season } = useSeasonStandings(pool.id)

  const latestWeekId = weeks?.[0]?.week_id ?? undefined
  const isSeason = selected === SEASON

  // Season view carries a "last week" column, so the most recent completed week
  // is fetched either way. When that IS the selected week both hooks resolve to
  // the same query key and React Query serves a single request.
  const { data: lastWeek } = useWeekResults(pool.id, latestWeekId)
  const { data: weekRows } = useWeekResults(pool.id, isSeason ? undefined : selected)
  const { data: winners } = useWeekWinners(pool.id, isSeason ? undefined : selected)

  // season_standings is the only one of these views carrying display_name, and
  // it covers exactly the same members — see api.ts.
  const nameById = new Map((season ?? []).map((row) => [row.user_id, row.display_name]))
  const lastWeekByUser = new Map((lastWeek ?? []).map((row) => [row.user_id, row]))

  const nameOf = (userId: string | null) =>
    (userId ? nameById.get(userId) : null) ?? 'Unknown member'

  const heading = (
    <header className="bg-card border-rule border-b px-6 py-5">
      <h1 className="font-display text-ink text-team">THE BOARD</h1>
      <p className="eyebrow mt-1.5">{pool.name}</p>
    </header>
  )

  if (weeksPending) {
    return (
      <>
        {heading}
        <p className="eyebrow px-6 py-8">Loading the board…</p>
      </>
    )
  }

  if (!weeks || weeks.length === 0) {
    return (
      <>
        {heading}
        <p className="text-ink-dim text-body px-6 py-8">
          Standings arrive after the first week is played.
        </p>
      </>
    )
  }

  const selectedWeek = weeks.find((week) => week.week_id === selected)
  const sortedWeekRows = weekRows ?? []

  const rows: BoardRow[] = isSeason
    ? (season ?? []).map((row) => ({
        userId: row.user_id,
        rank: row.season_rank ?? 0,
        correct: row.total_correct,
        incorrect: row.total_incorrect,
        note:
          (row.weeks_forfeited ?? 0) > 0
            ? pluralize(row.weeks_forfeited ?? 0, 'forfeit')
            : (row.total_ties ?? 0) > 0
              ? pluralize(row.total_ties ?? 0, 'tie')
              : null,
        noteIsWarning: (row.weeks_forfeited ?? 0) > 0,
      }))
    : sortedWeekRows.map((row) => ({
        userId: row.user_id,
        rank: weekRank(sortedWeekRows, row),
        correct: row.correct_picks,
        incorrect: row.incorrect_picks,
        note: row.forfeited
          ? 'Forfeit'
          : (row.tie_games ?? 0) > 0
            ? pluralize(row.tie_games ?? 0, 'tie')
            : null,
        noteIsWarning: Boolean(row.forfeited),
      }))

  return (
    <>
      {heading}

      <div className="px-6 pt-5">
        <label className="eyebrow block" htmlFor="board-scope">
          Showing
        </label>
        <select
          id="board-scope"
          value={selected}
          onChange={(event) => setSelected(event.target.value)}
          className="bg-card border-rule-strong text-ink focus:border-field text-body mt-1.5 w-full border px-3 py-2.5 font-semibold"
        >
          <option value={SEASON}>Season total</option>
          {weeks.map((week) => (
            <option key={week.week_id} value={week.week_id ?? ''}>
              {week.week_label}
            </option>
          ))}
        </select>
      </div>

      <div className="px-6 py-5">
        {/* Column labels in mono caps — same treatment as the kickoff slot
            headings on the Picks page. */}
        <div className="text-ink-dim text-eyebrow mb-2 flex items-center gap-3 font-mono tracking-[0.14em] uppercase">
          <span className="w-7">#</span>
          <span className="flex-1">Member</span>
          <span className="w-14 text-right">Record</span>
          <span className="w-12 text-right">Win%</span>
          {isSeason && <span className="w-14 text-right">Last</span>}
        </div>

        <ul>
          {rows.map((row) => {
            const previous = row.userId ? lastWeekByUser.get(row.userId) : undefined
            return (
              <li
                key={row.userId}
                className={cn(
                  'bg-card border-rule mb-2 flex items-center gap-3 border border-l-4 px-4 py-3',
                  rankAccent(row.rank),
                )}
              >
                <span className="text-ink text-score w-7 font-mono tabular-nums">{row.rank}</span>

                <span className="min-w-0 flex-1">
                  <span className="text-ink text-team block truncate font-semibold">
                    {nameOf(row.userId)}
                  </span>
                  {row.note && (
                    <span
                      className={cn(
                        'text-eyebrow font-mono tracking-[0.16em] uppercase',
                        row.noteIsWarning ? 'text-flag' : 'text-ink-dim',
                      )}
                    >
                      {row.note}
                    </span>
                  )}
                </span>

                <span className="text-ink text-team w-14 text-right font-mono font-bold tabular-nums">
                  {record(row.correct, row.incorrect)}
                </span>
                <span className="text-ink-dim text-body w-12 text-right font-mono tabular-nums">
                  {winPct(row.correct, row.incorrect)}
                </span>
                {isSeason && (
                  <span className="text-ink-dim text-body w-14 text-right font-mono tabular-nums">
                    {previous ? record(previous.correct_picks, previous.incorrect_picks) : '—'}
                  </span>
                )}
              </li>
            )
          })}
        </ul>

        {/* The standings answer "who won"; the week board answers "on what".
            Offered only on a week scope because there is no season equivalent
            of a games-by-members grid. */}
        {!isSeason && (
          <Link
            to={`/p/${pool.slug}/week`}
            className="border-rule bg-card text-ink hover:border-rule-strong text-body mt-1 flex items-center justify-between border px-4 py-3 font-semibold transition-colors"
          >
            See everyone&rsquo;s picks
            <span aria-hidden className="text-ink-dim">
              &rarr;
            </span>
          </Link>
        )}

        {/* Week winner card. Omitted on season total, and omitted when nobody
            scored — weekly_winners requires points > 0, so an unplayed or
            all-zero week legitimately has no winner rather than a false one. */}
        {!isSeason && winners && winners.length > 0 && (
          <div className="bg-card border-rule border-l-field mt-5 border border-l-4 px-4 py-4">
            <p className="eyebrow">
              {selectedWeek?.week_label ?? 'Week'} winner{winners.length > 1 ? 's' : ''}
            </p>
            <ul className="mt-2.5 flex flex-col gap-3">
              {winners.map((winner) => {
                const result = sortedWeekRows.find((row) => row.user_id === winner.user_id)
                return (
                  <li key={winner.user_id} className="flex items-baseline justify-between gap-3">
                    <span className="min-w-0 flex-1">
                      <span className="text-ink text-team block truncate font-semibold">
                        {nameOf(winner.user_id)}
                      </span>
                      <span className="eyebrow">
                        Tiebreaker · {result?.tiebreaker_prediction ?? '—'}
                      </span>
                    </span>
                    <span className="text-mark text-team font-mono font-bold tabular-nums">
                      {winner.points ?? 0} pts
                    </span>
                  </li>
                )
              })}
            </ul>
          </div>
        )}
      </div>
    </>
  )
}
